import { Button, Divider, Stack, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useCheckCuitAvailability, useCheckEmailAvailability, useOwnUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getGrowerIdentifier } from '/@/store/session/selectors';
import type { SidebarGrower } from '../index';
import CuitAlert from './cuit-alert';
import CuitTextField, { validateCuit } from './cuit-text-field';
import EmailIdentifierTextField, { validateGrowerIdentifierEmail } from './email-identifier-text-field';
import GrowerForm from './grower-form';
import { getGrowerIdentifierCopy, normalizeGrowerIdentifier } from './grower-identifier';

const AddGrowerSection = ({
    singleGrower = false,
    growers,
    editingGrowerIndex,
    onAddGrower,
    onUpdateGrower,
    defaultRv,
}: {
    growers: SidebarGrower[];
    singleGrower?: boolean;
    editingGrowerIndex: number | null;
    onAddGrower: (grower: SidebarGrower) => void;
    onUpdateGrower: (index: number, grower: SidebarGrower) => void;
    /** Default RV for the grower (Consultant creating from an RV's detail page). */
    defaultRv?: string;
}) => {
    const growerIdentifier = useSelector(getGrowerIdentifier);
    const copy = getGrowerIdentifierCopy(growerIdentifier);
    const isEmailIdentifier = growerIdentifier === 'email';

    const [identifierValue, setIdentifierValue] = useState('');
    const [identifierError, setIdentifierError] = useState<string | null>(null);
    /** Canonical value: what we send to the API and store on the sidebar grower. */
    const normalizedIdentifier = normalizeGrowerIdentifier(growerIdentifier, identifierValue);
    const cuitAvailabilityMutation = useCheckCuitAvailability();
    const emailAvailabilityMutation = useCheckEmailAvailability();
    const ownUserNodeQuery = useOwnUserNode();
    const availabilityData = isEmailIdentifier ? emailAvailabilityMutation.data : cuitAvailabilityMutation.data;
    const isMutating = isEmailIdentifier ? emailAvailabilityMutation.isMutating : cuitAvailabilityMutation.isMutating;

    const editingGrower = editingGrowerIndex !== null ? growers[editingGrowerIndex] : null;
    const isEditMode = Boolean(editingGrower?.editable && editingGrower?.id != null);

    const editingIdentifierValue = editingGrower
        ? isEmailIdentifier
            ? (editingGrower.email ?? '')
            : (editingGrower.cuit ?? '')
        : '';

    const resetAvailability = () => {
        if (isEmailIdentifier) {
            emailAvailabilityMutation.reset();
        } else {
            cuitAvailabilityMutation.reset();
        }
    };

    const handleValidateIdentifier = () => {
        if (availabilityData) {
            resetAvailability();
            return;
        }
        setIdentifierError(null);
        const validationError = isEmailIdentifier
            ? validateGrowerIdentifierEmail(identifierValue, ownUserNodeQuery.data?.email)
            : validateCuit(identifierValue);
        if (validationError) {
            setIdentifierError(validationError);
            return;
        }
        const request = isEmailIdentifier
            ? emailAvailabilityMutation.trigger(normalizedIdentifier)
            : cuitAvailabilityMutation.trigger(normalizedIdentifier);
        request.catch(() => {
            const message = copy.connectionError;
            setIdentifierError(message);
            enqueueSnackbar(message, { variant: 'error' });
        });
    };

    const resetIdentifier = () => {
        setIdentifierValue('');
        resetAvailability();
    };

    return (
        <Stack className='mp-mask' width='100%' spacing={3} alignItems='stretch'>
            <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                {_('Agregar nuevo productor')}
            </Typography>
            <Stack alignItems='start' width='100%'>
                <Typography variant='body1' fontWeight='bold' color='primary'>
                    {copy.step1Title}
                </Typography>
                <Typography variant='body2' color='primary.mui.inactive'>
                    {copy.step1Subtitle}
                </Typography>
            </Stack>
            <Stack direction='row' spacing={2} width='100%' alignItems='flex-start'>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                    {isEmailIdentifier ? (
                        <EmailIdentifierTextField
                            value={isEditMode ? editingIdentifierValue : identifierValue}
                            onChange={(email) => {
                                setIdentifierError(null);
                                setIdentifierValue(email);
                            }}
                            disabled={isEditMode || !!availabilityData}
                            error={!!identifierError}
                            helperText={identifierError ?? undefined}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleValidateIdentifier();
                                }
                            }}
                        />
                    ) : (
                        <CuitTextField
                            value={isEditMode ? editingIdentifierValue : identifierValue}
                            onChange={(digits) => {
                                setIdentifierError(null);
                                setIdentifierValue(digits);
                            }}
                            disabled={isEditMode || !!availabilityData}
                            error={!!identifierError}
                            helperText={identifierError ?? undefined}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleValidateIdentifier();
                                }
                            }}
                        />
                    )}
                </Stack>
                {!isEditMode && (
                    <Button
                        type='button'
                        variant='outlined'
                        color='secondary'
                        sx={{ flex: 'none' }}
                        onClick={handleValidateIdentifier}
                        loading={isMutating}
                        disabled={isMutating}
                    >
                        {availabilityData ? copy.changeButton : copy.validateButton}
                    </Button>
                )}
            </Stack>
            {isEditMode && editingGrower ? (
                <GrowerForm
                    singleGrower={singleGrower}
                    identifierDisplay={editingIdentifierValue}
                    mode='edit'
                    editNodeId={editingGrower.id}
                    initialValues={{
                        name: editingGrower.name ?? '',
                        segment: editingGrower.segment === 'premium' ? 'premium' : 'common',
                        advisor: editingGrower.advisor ?? '',
                        location: editingGrower.location ?? '',
                        email: editingGrower.email ?? '',
                        phone: editingGrower.phone ?? '',
                    }}
                    onSubmit={(data) => {
                        if (editingGrowerIndex !== null) {
                            onUpdateGrower(editingGrowerIndex, {
                                ...editingGrower,
                                name: data.name,
                                segment: data.segment,
                                advisor: data.advisor,
                                location: data.location,
                                email: data.email,
                                phone: data.phone,
                            });
                        }
                    }}
                />
            ) : availabilityData?.status === 'available' ? (
                <GrowerForm
                    identifierDisplay={identifierValue}
                    singleGrower={singleGrower}
                    initialValues={
                        isEmailIdentifier
                            ? { email: normalizedIdentifier, ...(defaultRv ? { rv: defaultRv } : {}) }
                            : defaultRv
                              ? { rv: defaultRv }
                              : undefined
                    }
                    onSubmit={(data, createdNode) => {
                        onAddGrower({
                            id: createdNode?.id,
                            name: data.name,
                            cuit: isEmailIdentifier ? undefined : normalizedIdentifier,
                            email: isEmailIdentifier ? normalizedIdentifier : data.email,
                            segment: data.segment,
                            advisor: data.advisor,
                            rv: data.rv,
                            editable: true,
                            location: data.location,
                            phone: data.phone,
                        });
                        resetIdentifier();
                    }}
                />
            ) : (
                availabilityData && (
                    <CuitAlert
                        availabilityOutput={availabilityData}
                        identifierValue={identifierValue}
                        onInvitationSent={(invitation) => {
                            const base = isEmailIdentifier
                                ? { email: normalizedIdentifier, editable: false as const }
                                : { cuit: normalizedIdentifier, editable: false as const };
                            if (invitation.invitedBy === 'advisor') {
                                onAddGrower({
                                    ...base,
                                    rv: invitation.rv.name,
                                });
                            } else {
                                onAddGrower({
                                    ...base,
                                    advisor: invitation.advisor.name,
                                });
                            }
                            resetIdentifier();
                        }}
                    />
                )
            )}
            <Divider />
        </Stack>
    );
};

export default AddGrowerSection;
